import {Injectable} from '@angular/core';
import {ConnectorMessage} from './connector-message';
import {ObjectExtractingServiceService} from './object-extracting-service.service';
import {CardWebService} from './card-web.service';
import {GameDesignService} from '../design-recommender/shared/game-design.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {BehaviorSubject, Observable} from 'rxjs';
import {CustomObjectsService} from './custom-objects.service';
import {filter, map} from 'rxjs/operators';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {AuthService} from './auth.service';
import {ConnectorMessageType} from './connector-message.type';
import {MessageFactoryService} from './message-factory.service';

// Change URL for testing purpose of Java Backend ws://localhost:8080/ and secure = false in proxy.conf
const SOCKET_BASE_URL = '/design/';

@Injectable({
  providedIn: 'root'
})
export class CardWebConnectorService {
  private socket: WebSocket;
  private userId: string;

  private heartBeat$: BehaviorSubject<ConnectorMessage> = new BehaviorSubject<ConnectorMessage>(null);

  private designIdChangeEvent$: BehaviorSubject<string>;
  private designShareOption$: BehaviorSubject<string>;

  private RECONNECT_COUNTER = 0;
  private MAX_FAST_RECONNECTS = 5;

  private noReconnects: any;
  private RECONNECT_INTERVAL = 5000;

  private currentDesignId: string;
  private currentShareOption: string;

  private webSocketEvent$: BehaviorSubject<ConnectorMessage>;

  private statusSubject: BehaviorSubject<string> = new BehaviorSubject<string>('AutoSave nicht verbunden');
  public status$: Observable<string> = this.statusSubject.asObservable();
  private timestamp = 'original';

  constructor(private auth: AuthService,
              private activatedRoute: ActivatedRoute,
              private router: Router,
              private oes: ObjectExtractingServiceService,
              private cws: CardWebService,
              private gs: GameDesignService,
              private cos: CustomObjectsService,
              private mfs: MessageFactoryService,
              private snackBar: MatSnackBar) {
    this.designIdChangeEvent$ = new BehaviorSubject<string>(this.currentDesignId);
    this.designShareOption$ = new BehaviorSubject<string>(this.currentShareOption);
    this.auth.getUserDataObservable().subscribe(userdata => {
      if (userdata != null && userdata.userData != null) {
        this.userId = userdata.userData.sub;
        this.router.events.pipe(
          // identify navigation end
          filter((event) => event instanceof NavigationEnd),
          // now query the activated route
          map(() => this.getDeepestChild(this.activatedRoute)),
          filter((route: ActivatedRoute) => route.outlet === 'primary'),
        ).subscribe((route: ActivatedRoute) => {
          if (route.snapshot.paramMap.has('timestamp')){
            if (route.snapshot.paramMap.has('design')) {
              const design = route.snapshot.paramMap.get('design');
              const timestamp = route.snapshot.paramMap.get('timestamp');
              this.setDesignIdAndReconnect(design, timestamp);
            }
          }
        });

        /*this.router.events.pipe(
          filter(evt => evt instanceof NavigationEnd),
          switchMap(() => this.getDeepestChild(this.activatedRoute).paramMap),
          map(pm => pm.get('design')),
          filter(designId => !!designId),
          distinctUntilChanged(),
        )
          .subscribe(designId => {
            console.log('Switched to design:', designId);
            this.setDesignIdAndReconnect(designId);
          });*/
      }
    });
    this.webSocketEvent$ = new BehaviorSubject<ConnectorMessage>(this.mfs.createEmptyMessage());
    this.oes.getMessageBehaviorSubject().subscribe(message => {
      this.handleAction(message);
    });
    let intervalId;
    this.getHeartBeatBehaviorSubject().subscribe( heartBeatMessage => {
      if (heartBeatMessage !== null) {
        intervalId = setInterval(() => {
          this.sendMessage(heartBeatMessage);
        }, 10000);
      }else {
        if (intervalId !== undefined && intervalId !== null) {
          clearInterval(intervalId);
        }
      }
    });
  }

  public setDesignIdAndReconnect(newId: string, timestamp: string) {
    if (this.currentDesignId === newId && this.timestamp === timestamp) {
      return;
    }
    this.currentDesignId = newId;
    this.timestamp = timestamp;
    this.designIdChangeEvent$.next(decodeURIComponent(this.currentDesignId));
    this.socket?.close();
    this.connect(); // Will use the new designId

  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  public getDesignIdChangeEvent() {
    return this.designIdChangeEvent$.asObservable();
  }

  public getDesignShareOption() {
    return this.designShareOption$.asObservable();
  }

  public getConnectorMessageEvent() {
    return this.webSocketEvent$.asObservable();
  }


  private connect() {
    if (!this.currentDesignId) {
      console.log('no design specified');
      return;
    }
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already open.');
      return; // WebSocket is already connected
    }

    if (this.socket && this.socket.readyState === WebSocket.CONNECTING) {
      console.log('WebSocket connection is in progress.');
      return; // WebSocket is in the process of connecting
    }

    if (this.socket) {
      this.socket.close(); // Close the existing socket if not already closed
    }

    try {
      console.log('::::::INITIALIZE WEBSOCKET:::::::');
      this.socket = new WebSocket(SOCKET_BASE_URL + this.userId + `/${this.currentDesignId}` + `/${this.timestamp}`);
    } catch (e) {
      console.log(':::::::::::.init errror');
      this.scheduleReconnect();
    }

    this.socket.onopen = () => {
      console.log('WebSocket connection opened.');

      this.statusSubject.next('AutoSave verbunden');

      if (this.noReconnects && this.socket.readyState === WebSocket.OPEN) {
        this.noReconnects = false;
        this.snackBar.open('Verbindung wieder hergestellt', '',
          {
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            duration: 3000
          });
      }
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);

      this.statusSubject.next('AutoSave nicht verbunden');

      this.noReconnects = false;
      this.scheduleReconnect();
    };

    // unexpected websocket close
    this.socket.onclose = (event) => {
      console.log('WebSocket connection closed:', event);

      this.statusSubject.next('AutoSave nicht verbunden');
      if (event.code === 1000) {
        return;
      }
      if (event.code === 1003) {
        this.noReconnects = true;
        this.router.navigate(['/']);
        this.snackBar.open('Zugriff nicht möglich', '',
          {
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            duration: 3000
          });
        return;
      }
      this.scheduleReconnect();
    };

    this.socket.onmessage = (event: MessageEvent) => {
      const message: ConnectorMessage = JSON.parse(event.data);
      if (message.connectorMessageType === ConnectorMessageType.SAVE_MESSAGE) {
        // User receives initial cardWeb as he enters the design
        this.currentShareOption = message.shareOption;
        this.designShareOption$.next(this.currentShareOption);
        this.cws.loadCardWebByServer(message.cardWeb);
        this.gs.loadGameDesignByServer(message.gameDesign);
        this.cos.loadCustomObjectsByServer(message.customObjects);
      }
      this.handleIncomingMessage(message);
    };
  }

  private handleIncomingMessage(message: ConnectorMessage) {
    if (message.userId === null || message.userId === undefined) {
      console.error('Invalid message without userId received', message);
    }
    // Filter own messages out
    if (message.connectorMessageType !== ConnectorMessageType.SAVE_MESSAGE && this.userId === message.userId) {
      return;
    }
    this.webSocketEvent$.next(message);
  }

  private scheduleReconnect() {
    if (!this.noReconnects) {
      if (this.socket.readyState === WebSocket.OPEN) {
        return;
      }
      setTimeout(() => {
        console.log('Attempting to reconnect WebSocket...');

        this.statusSubject.next('AutoSave Verbindungsaufbau');


        this.connect();
        this.RECONNECT_COUNTER++;
        if (this.RECONNECT_COUNTER > this.MAX_FAST_RECONNECTS) {
          this.RECONNECT_INTERVAL = 30000;
        }
      }, this.RECONNECT_INTERVAL);
    }
  }

  private sendMessage(message: ConnectorMessage) {
    if (this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  public handleAction(message: ConnectorMessage) {
    if (message == null) {
      return;
    }
    message.customObjects = this.cos.getCustomObjects();
    message.cardWeb = this.cws.getCustomCardWeb();
    message.gameDesign = this.gs.getGameDesign();
    this.sendMessage(message);

    // Send Heartbeat for reclaiming locking
    switch (message.connectorMessageType) {
      case ConnectorMessageType.ENTER_CARD_NOTE:
      case ConnectorMessageType.ENTER_LINK_NOTE:
      case ConnectorMessageType.ENTER_CARD_LINK:
        this.startHeartbeatLocking(message);
        break;
      case ConnectorMessageType.LEAVE_CARD_NOTE:
      case ConnectorMessageType.LEAVE_LINK_NOTE:
      case ConnectorMessageType.LEAVE_CARD_LINK:
        this.stopHeartbeatLocking();
        break;
      default:
    }
    return;
  }

  private startHeartbeatLocking(message: ConnectorMessage) {
    const heartBeatMessage = this.mfs.createMessageHeartBeatLock(message);
    this.heartBeat$.next(heartBeatMessage);
  }

  private stopHeartbeatLocking() {
    this.heartBeat$.next(null);
  }

  private getHeartBeatBehaviorSubject() {
    return this.heartBeat$.asObservable();
  }
}
